import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AppLayout } from '../components/Layout';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen, Plus, Trash2, ChevronDown, CheckCircle2, Clock, Circle,
  Award, GraduationCap, X, Edit3, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'planned', label: 'Planned', color: 'text-slate-300', bg: 'bg-slate-700/30' },
  { value: 'in_progress', label: 'In Progress', color: 'text-amber-400', bg: 'bg-amber-500/10' },
  { value: 'complete', label: 'Complete', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
];

const CAT_COLORS = {
  english: 'bg-blue-500/15 border-blue-500/30 text-blue-300',
  math: 'bg-violet-500/15 border-violet-500/30 text-violet-300',
  science: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300',
  social_studies: 'bg-amber-500/15 border-amber-500/30 text-amber-300',
  health_pe: 'bg-green-500/15 border-green-500/30 text-green-300',
  elective: 'bg-slate-600/30 border-slate-500/30 text-slate-300',
  cte: 'bg-cyan-500/15 border-cyan-500/30 text-cyan-300',
  arts: 'bg-rose-500/15 border-rose-500/30 text-rose-300',
  world_lang: 'bg-orange-500/15 border-orange-500/30 text-orange-300',
  ap_ib: 'bg-purple-500/15 border-purple-500/30 text-purple-300',
};

function AddCourseForm({ categories, suggestions, onClose, onAdded }) {
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState({ name: '', category: 'english', grade: 9, semester: 1, credits: 1, status: 'planned', is_cte: false, is_ap: false });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const catSuggestions = suggestions[form.category] || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error('Course name required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/course-planner/courses`, form, getAuthHeader());
      toast.success('Course added');
      onAdded();
      onClose();
    } catch { toast.error('Could not add course'); }
    finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#0a1729]/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold">Add Course</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-300 hover:text-white" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs text-slate-300 mb-1 block">Course Name *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} required
              list="course-suggestions"
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
              placeholder="e.g. AP Biology" />
            <datalist id="course-suggestions">
              {catSuggestions.map(s => <option key={s} value={s} />)}
            </datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Category</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
                {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
                {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Grade</label>
              <select value={form.grade} onChange={e => set('grade', parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
                {[9,10,11,12].map(g => <option key={g} value={g}>{g}th</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Semester</label>
              <select value={form.semester} onChange={e => set('semester', parseInt(e.target.value))}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
                <option value={1}>Fall</option>
                <option value={2}>Spring</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-300 mb-1 block">Credits</label>
              <select value={form.credits} onChange={e => set('credits', parseFloat(e.target.value))}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white">
                {[0.5, 1, 1.5, 2].map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={form.is_cte} onChange={e => set('is_cte', e.target.checked)} className="rounded" />
              CTE Course
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input type="checkbox" checked={form.is_ap} onChange={e => set('is_ap', e.target.checked)} className="rounded" />
              AP / Dual Enrollment
            </label>
          </div>
          <button type="submit" disabled={saving} data-testid="add-course-btn"
            className="w-full py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-semibold text-sm disabled:opacity-60">
            {saving ? 'Adding...' : 'Add Course'}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

export default function CoursePlannerPage() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/course-planner`, getAuthHeader())
      .then(r => setData(r.data))
      .catch(() => toast.error('Could not load course plan'))
      .finally(() => setLoading(false));
  }, [getAuthHeader]);

  useEffect(() => { load(); }, []);

  const deleteCourse = async (id) => {
    try {
      await axios.delete(`${API}/course-planner/courses/${id}`, getAuthHeader());
      toast.success('Course removed');
      load();
    } catch { toast.error('Could not remove'); }
  };

  const courses = data?.courses || [];
  const cats = data?.categories || [];
  const credits = data?.credits_by_category || {};
  const total = data?.total_credits_earned || 0;

  const gradeGroups = [9, 10, 11, 12].map(g => ({
    grade: g,
    label: `${g}th Grade`,
    fall: courses.filter(c => c.grade === g && c.semester === 1),
    spring: courses.filter(c => c.grade === g && c.semester === 2),
  }));

  const statusIcon = (status) => {
    if (status === 'complete') return <CheckCircle2 className="w-3 h-3 text-emerald-400" />;
    if (status === 'in_progress') return <Clock className="w-3 h-3 text-amber-400" />;
    return <Circle className="w-3 h-3 text-slate-300" />;
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-testid="course-planner-page">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-4 rounded-xl border border-cyan-400/25 bg-cyan-500/5 p-3.5 flex flex-wrap items-center justify-between gap-3"
            style={{ boxShadow: '0 20px 60px -50px rgba(34,211,238,0.5)' }}>
            <div className="flex items-start gap-2">
              <GraduationCap className="w-4 h-4 text-cyan-300 mt-0.5" />
              <div>
                <p className="text-sm text-white font-semibold">Try the new Academic Alignment page</p>
                <p className="text-xs text-slate-300">
                  See what courses at your school best support your CDP direction, instead of building a schedule from scratch.
                </p>
              </div>
            </div>
            <a href="/app/academic-alignment"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs text-white bg-gradient-to-r from-cyan-500 to-violet-500 hover:opacity-95 transition-opacity">
              Open Academic Alignment <ArrowRight className="w-3 h-3" />
            </a>
          </div>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <GraduationCap className="w-4 h-4 text-violet-400" />
                <span className="text-xs font-semibold uppercase tracking-widest text-violet-400">Academic Planning</span>
              </div>
              <h1 className="text-4xl font-bold text-white">4-Year Course Plan</h1>
              <p className="text-slate-300 text-sm mt-1">Plan your academic journey from 9th through 12th grade. NC requires 28 credits to graduate.</p>
            </div>
            <button onClick={() => setShowAdd(true)} data-testid="add-course-open-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold shadow-lg transition-all hover:scale-105">
              <Plus className="w-4 h-4" /> Add Course
            </button>
          </div>
        </motion.div>

        {/* Credit progress bar */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="rounded-xl border border-slate-700/50 bg-slate-800/40 p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-white font-semibold">Total Credits</p>
              <p className="text-slate-300 text-xs">Earned or in progress</p>
            </div>
            <div className="text-right">
              <span className="text-3xl font-bold text-white">{total}</span>
              <span className="text-slate-300 text-lg"> / 28</span>
            </div>
          </div>
          <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(100, total/28*100)}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
              className="h-full rounded-full bg-gradient-to-r from-violet-500 to-cyan-500" />
          </div>
          {/* Category breakdown */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
            {cats.slice(0, 5).map(cat => {
              const cred = credits[cat.id] || { earned: 0, planned: 0, required: cat.required_credits, on_track: false };
              const pct = cat.required_credits > 0 ? Math.min(100, (cred.earned / cat.required_credits) * 100) : 100;
              return (
                <div key={cat.id} className="text-center">
                  <div className={`text-xs px-2 py-1 rounded border font-medium mb-1 ${CAT_COLORS[cat.id] || 'bg-slate-700 text-slate-300 border-slate-600'}`}>
                    {cat.label}
                  </div>
                  <div className="h-1 bg-slate-700 rounded-full overflow-hidden mb-1">
                    <div className="h-full bg-current rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <p className={`text-[10px] ${cred.on_track || cat.required_credits === 0 ? 'text-emerald-400' : 'text-slate-300'}`}>
                    {cred.earned + cred.planned}/{cat.required_credits}
                  </p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* 4-Year Grid */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
            {gradeGroups.map((g, gi) => (
              <motion.div key={g.grade} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: gi * 0.08 }}
                className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
                  <h3 className="text-white font-semibold text-sm">{g.label}</h3>
                  <span className="text-xs text-slate-300">
                    {courses.filter(c => c.grade === g.grade).length} courses
                  </span>
                </div>
                <div className="p-3 space-y-3">
                  {[{sem: 1, courses: g.fall, label: 'Fall'}, {sem: 2, courses: g.spring, label: 'Spring'}].map(s => (
                    <div key={s.sem}>
                      <p className="text-[10px] uppercase tracking-wide text-slate-300 font-semibold mb-1.5">{s.label}</p>
                      <div className="space-y-1.5">
                        {s.courses.length === 0 ? (
                          <p className="text-slate-300 text-xs italic">No courses planned</p>
                        ) : s.courses.map(c => (
                          <div key={c.id} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs group ${CAT_COLORS[c.category] || 'bg-slate-700 text-slate-300 border-slate-600'}`}>
                            {statusIcon(c.status)}
                            <span className="flex-1 font-medium truncate">{c.name}</span>
                            {c.is_ap && <span className="text-[9px] opacity-70">AP</span>}
                            {c.is_cte && <span className="text-[9px] opacity-70">CTE</span>}
                            <button onClick={() => deleteCourse(c.id)} className="opacity-0 group-hover:opacity-100 hover:text-red-400 transition-all">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {courses.length === 0 && !loading && (
          <div className="text-center py-8 text-slate-300">
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-slate-300 font-medium mb-1">Your 4-year plan is empty</p>
            <p className="text-sm mb-4">Add courses to track your path to graduation</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showAdd && data && (
          <AddCourseForm categories={data.categories} suggestions={data.suggestions} onClose={() => setShowAdd(false)} onAdded={load} />
        )}
      </AnimatePresence>
    </AppLayout>
  );
}
